/**
 * Where a grid's stat-card numbers come from.
 *
 * Every odl_app grid serves its aggregate counts at `<resource>/stats/`, so a
 * page opting into descriptor cards points at a resource rather than wiring a
 * fetch: the grid already knows its base path and derives the rest. A caller
 * override stays for the resource whose numbers live somewhere other than the
 * conventional path.
 *
 * Contains:
 * - odlResourcePath   — base path → the `odl-app/`-prefixed resource path
 * - statsPathFor      — base path → its conventional stats path
 * - resolveStatsFetcher — the fetch a grid should use, override first
 *
 * Returns plain JS — no JSX, no redux.
 */

const ODL_APP_PREFIX = 'odl-app/';

/**
 * The `odl-app/`-prefixed path for a grid's resource. A base path that already
 * carries the prefix is left alone, so a caller may pass either form.
 *
 * @param {string} basePath - e.g. `'sites'` or `'odl-app/sites/1/cameras'`.
 * @returns {string}
 */
export function odlResourcePath(basePath) {
  return basePath.startsWith(ODL_APP_PREFIX) ? basePath : `${ODL_APP_PREFIX}${basePath}`;
}

/**
 * The conventional stats path for a grid's resource.
 *
 * @param {string} basePath
 * @returns {string} e.g. `'odl-app/sites/stats'`.
 */
export function statsPathFor(basePath) {
  return `${odlResourcePath(basePath)}/stats`;
}

/**
 * The stats fetch a grid should run: the caller's override when it supplied
 * one, else a fetch of the conventional path through the injected client.
 * Null when neither is available — a grid with no way to fetch shows its cards
 * loading rather than calling nothing.
 *
 * @param {object} args
 * @param {string} args.basePath - The grid's resource path.
 * @param {Function} [args.fetchStats] - Caller override `(params) => Promise<object>`.
 * @param {Function} [args.fetchStatsFn] - Injected client `(path, params) => Promise<object>`.
 * @returns {?(params: object) => Promise<object>}
 */
export function resolveStatsFetcher({ basePath, fetchStats, fetchStatsFn }) {
  if (fetchStats) return fetchStats;
  if (!fetchStatsFn) return null;
  const path = statsPathFor(basePath);
  return params => fetchStatsFn(path, params);
}
